import React from 'react'
import PropTypes from 'prop-types'

import {
  onHideNavFieldForFund,
  isPresented,
  hasLiteratureContent,
  getEtfPremDiscMessage,
} from './Helpers/FundUtility.js'
import Loader from './Modules/Shared/Loader.jsx'
import FundHeaderMessage from './Modules/FundHeader/FundHeaderMessage.jsx'
import { BlogArticleList } from '../../spa/components/blog/blogSimpleArticleList/BlogArticleList.jsx'
import {
  ETF_FUND,
  ETF_INDEX_FUND,
  VARIABLE_FUND,
  CLOSED_END_FUND,
} from './Helpers/FundTypes.js'
import Overview from './Modules/Overview/Overview.jsx'
import Performance from './Modules/Performance/Performance.jsx'
import RatingsRisk from './Modules/RatingsRisk/RatingsRisk.jsx'
import FeesExpenses from './Modules/FeesExpenses.jsx'
import PortfolioDetails from './Modules/Portfolio/PortfolioDetails.jsx'
import CapitalGains from './Modules/CapitalGains/CapitalGains.jsx'
import Literature from './Modules/Literature/Literature.jsx'
import RelatedProducts from './Modules/RelatedProducts.jsx'
import FundDisclosures from './Modules/FundDisclosures/FundDisclosures.jsx'
import FundHeader from './Modules/FundHeader/FundHeader.jsx'
import BrandMessage from './Modules/BrandMessage.jsx'
import FundViewer from './Modules/MutualFundViewer/FundViewer.jsx'
import './index.css'

export default function FundDetails(props) {
  const {
    fund,
    productList,
    fullHoldings,
    etfRegulatoryMessage,
    subscriptionModel,
    setFundSubscription,
    setShowEmailSubscriptionCenterModal,
    getFundSubscriptionsActiveLiterature,
    page,
  } = props

  const { fundSubscription, subscriptionProfile, showEmailSubscriptionModal } =
    subscriptionModel

  const navFields = {
    hidePerformance: false,
    hideRatingsAndRisk: false,
    hideFeesAndExpenses: false,
    hidePortfolio: false,
    hideCapitalGains: false,
    hideLiterature: false,
    hideRelatedProducts: false,
  }

  let etfPremDiscMessage = null

  const hidePortfolioBecauseOfFullHoldings = !!(
    fullHoldings &&
    fullHoldings.value &&
    fullHoldings.value.holdingDetail &&
    fullHoldings.value.holdingDetail.fullHoldings === null
  )

  if (fund) {
    const hideRatingsAndRiskBecauseNoDataClosedEnd =
      fund.fundGroup === CLOSED_END_FUND &&
      !isPresented(fund, 'morningStarRatingDetail') &&
      !isPresented(fund, 'morningStarEquityStyleBox') &&
      !isPresented(fund, 'threeYrAvgAnnRetStdDev') &&
      !isPresented(fund, 'riskDetail')

    navFields.hideOverview = onHideNavFieldForFund(fund, 'overview')
    navFields.hidePerformance = onHideNavFieldForFund(fund, 'performance')
    navFields.hideRatingsAndRisk =
      onHideNavFieldForFund(fund, 'ratings-risk') ||
      hideRatingsAndRiskBecauseNoDataClosedEnd ||
      (fund.morningStarRatingDetail ||
        (fund.risk && fund.threeYrAvgAnnRetStdDev) ||
        fund.fundGroup === ETF_FUND) === false
    navFields.hideFeesAndExpenses =
      onHideNavFieldForFund(fund, 'fees-expenses') ||
      fund.feesAndExpenseDetail === null ||
      (fund.feesAndExpenseDetail !== null &&
        fund.feesAndExpenseDetail.grossExpense === '-' &&
        fund.feesAndExpenseDetail.expenseNet === '-' &&
        fund.feesAndExpenseDetail.maxSalesCharge === '-')
    navFields.hidePortfolio =
      onHideNavFieldForFund(fund, 'portfolio-details') ||
      hidePortfolioBecauseOfFullHoldings
    navFields.hideCapitalGains =
      onHideNavFieldForFund(fund, 'capital-gains') ||
      fund.fundGroup === VARIABLE_FUND
    navFields.hideLiterature =
      onHideNavFieldForFund(fund, 'literature') ||
      !hasLiteratureContent(fund.literature)
    navFields.hideRelatedProducts = onHideNavFieldForFund(
      fund,
      'related-products'
    )
    etfPremDiscMessage =
      fund.fundGroup === ETF_FUND
        ? getEtfPremDiscMessage(fund, etfRegulatoryMessage)
        : null
  }

  const mergerDate =
    fund &&
    fund.messageContents &&
    fund.messageContents.mergerFund &&
    fund.messageContents.mergerFund.mergerDate

  return fund ? (
    fund?.cusip ? (
      <>
        <FundViewer fund={fund} productList={productList} />
        <div className="details-bg u-posRelative fund-details" id="new-details">
          {!mergerDate && (
            <BrandMessage
              fund={fund}
              isEtfIndex={fund.fundGroup === ETF_INDEX_FUND}
            />
          )}
          <FundHeader
            fund={fund}
            navFields={navFields}
            showEmailSubscriptionModal={
              subscriptionModel.showEmailSubscriptionModal
            }
            setShowEmailSubscriptionCenterModal={
              setShowEmailSubscriptionCenterModal
            }
            fundSubscriptionDocs={getFundSubscriptionsActiveLiterature()}
            mergerDate={mergerDate}
            productList={productList}
          />

          <FundHeaderMessage fund={fund} />

          {!mergerDate && (
            <>
              {!navFields.hideOverview && (
                <Overview fund={fund} investorFundDetail={fund} />
              )}

              {!navFields.hidePerformance && (
                <Performance
                  etfPremDiscMessage={etfPremDiscMessage}
                  fund={fund}
                />
              )}

              {!navFields.hideRatingsAndRisk && <RatingsRisk fund={fund} />}

              {!navFields.hideFeesAndExpenses && (
                <FeesExpenses
                  fund={fund}
                  feesAndExpenses={fund.feesAndExpenseDetail}
                />
              )}

              {!navFields.hidePortfolio && (
                <PortfolioDetails fund={fund} fullHoldings={fullHoldings} />
              )}

              {!navFields.hideCapitalGains && (
                <CapitalGains
                  fund={fund}
                  subscriptionProfile={subscriptionProfile}
                  fundSubscription={fundSubscription}
                  setFundSubscription={setFundSubscription}
                  showEmailSubscriptionModal={showEmailSubscriptionModal}
                />
              )}

              {!navFields.hideLiterature && (
                <Literature
                  fund={fund}
                  fundSubscription={fundSubscription}
                  setFundSubscription={setFundSubscription}
                  subscriptionProfile={subscriptionProfile}
                  showEmailSubscriptionModal={showEmailSubscriptionModal}
                  setShowEmailSubscriptionCenterModal={
                    setShowEmailSubscriptionCenterModal
                  }
                />
              )}

              <div className="u-bgColorLight u-cf">
                <BlogArticleList
                  page={page}
                  component={{
                    getParameters: () => ({
                      subscriptionButtonStyle: 'Tertiary',
                      subscription: 'Blog: Latest Insights',
                      sortOrder: 'desc',
                      title: 'Latest Insights',
                      scope:
                        '/content/documents/cti-blog-spa/latest-insights/summary/',
                      pageSize: 3,
                    }),
                  }}
                />
              </div>

              {!navFields.hideRelatedProducts && fund.relatedProducts && (
                <RelatedProducts relatedProducts={fund.relatedProducts} />
              )}

              <FundDisclosures fund={fund} />
            </>
          )}
          <div id="ampf-trace-loaded-page"></div>
        </div>
      </>
    ) : null
  ) : (
    <div className="u-bgColorOffwhite" style={{ width: '100%', height: 600 }}>
      <Loader />
    </div>
  )
}

FundDetails.propTypes = {
  fund: PropTypes.object,
}
